import { getCookie } from 'utils/get-cookie';
import { setCookie } from 'utils/set-cookie';
import { paramsFromQuerystring } from 'utils/params-from-querystring';

// Meta's Click ID, which the server forwards to the Conversions API as
// user_data.fbc (see fb_browser_data in conversion-tracking.php).
//
// Normally the Meta pixel handles this on its own: an ad click lands with
// ?fbclid=... in the URL and the pixel writes it into the _fbc cookie. Because
// the API is served from this same domain, that cookie rides along on every
// request and the click id reaches Meta without anyone doing anything.
//
// It silently stops working whenever the pixel does not run, or does not run in
// time — an ad blocker, a slow GTM load, a consent gate. The click id is then
// lost permanently, because fbclid only ever appears on that one landing URL.
// The conversion still gets sent; it just goes without the single strongest
// matching signal it could have carried.
//
// So we write the same cookie ourselves, in Meta's documented format, without
// waiting to see whether the pixel manages it. Same name and same value, so the
// pixel and the Conversions API agree on the click id and nothing downstream
// has to care which of the two wrote it.

const FBC_COOKIE = '_fbc';

// Matches the pixel's own expiry. A shorter one would quietly drop attribution
// for anyone who clicks an ad and converts weeks later.
const FBC_TTL_DAYS = 90;

// fb.{subdomainIndex}.{creationTimeMs}.{fbclid}
//
// subdomainIndex is 1 because set-cookie.js writes to .jpbouvetmethod.com, the
// registrable domain, which is where the pixel writes it too. A mismatch here
// makes Meta read the cookie as belonging to a different scope.
//
// The timestamp is MILLISECONDS. Seconds parse as a click from 1970, and Meta
// discards the attribution — the event still arrives and still counts, it just
// stops counting as ad-driven, which is the exact thing this file exists to fix.
const buildFbc = fbclid => `fb.1.${Date.now()}.${fbclid}`;

// Pull the click id back out of an existing cookie so a fresh ad click can
// replace an older one. fbclid values can themselves contain dots, so keep
// everything past the third separator rather than taking a single segment.
const fbclidFromCookie = value => {
    const parts = (value || '').split('.');

    return parts.length >= 4 ? parts.slice(3).join('.') : null;
};

export const captureFbc = () => {
    const { fbclid } = paramsFromQuerystring();

    if (!fbclid) {
        return;
    }

    // Don't rewrite on every page load of the same visit. The timestamp is the
    // click time, and refreshing it makes a week-old click look like it just
    // happened, which misattributes the conversion window. Only write when this
    // is a click we haven't already stored.
    if (fbclidFromCookie(getCookie(FBC_COOKIE)) === fbclid) {
        return;
    }

    setCookie(FBC_COOKIE, buildFbc(fbclid), FBC_TTL_DAYS);
};
